import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { EstablishmentsCheckoutService } from '../../order-checkout/establishments-checkout.service';
import { AppRequestContextService } from '../../../app-request-context.service';
import { AnalyticsService } from '@pedix-workspace/pedixapp-core-services';
import { SeoService } from '../../main/seo.service';
import { EstablishmentWorkingHoursService } from '../../main/establishment-working-hours.service';
import { ApiV1_Category } from '@pedix-workspace/api-v1';
import {
  PropInSelectedLanguagePipe,
  IsCategoryAvailablePipe,
  getUniqueTagsWithQuantities,
  EstablishmentUrlPipe,
} from '@pedix-workspace/pedixapp-shared-pipes';
import { ShowOrderComponent } from '../show-order/show-order.component';
import {
  CategoryAvailabilityDialogComponent,
  ItemListComponent,
  ItemListProductGroupingStrategyPipe,
  ProductListItemComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import {
  IconBackComponent,
  IconClockComponent,
  IconFilterComponent,
} from '@pedix-workspace/angular-ui-icons';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ProductFiltersDialogComponent } from './product-filters/product-filters-dialog.component';
import { AppSearchResultsService } from '../../../app-search-results.service';
import { StockIsOutOfStockPipe } from '@pedix-workspace/pedixapp-stock';
import { ShareButtonComponent } from '../../main/share-button/share-button.component';
import { CartPromotionsService } from '../../../cart-promotions.service';

@Component({
  selector: 'app-category',
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    RouterLink,
    IconBackComponent,
    IconClockComponent,
    IconFilterComponent,
    AlertComponent,
    ButtonComponent,
    CategoryAvailabilityDialogComponent,
    ProductListItemComponent,
    ShowOrderComponent,
    AsyncPipe,
    PropInSelectedLanguagePipe,
    IsCategoryAvailablePipe,
    ItemListComponent,
    ProductFiltersDialogComponent,
    NgTemplateOutlet,
    StockIsOutOfStockPipe,
    ShareButtonComponent,
  ],
})
export class CategoryComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private analytics = inject(AnalyticsService);
  private seoService = inject(SeoService);
  private appRequestContextService = inject(AppRequestContextService);
  private establishmentsCheckoutService = inject(EstablishmentsCheckoutService);
  readonly purchaseOptions = this.establishmentsCheckoutService.purchaseOptions;
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private searchResultsService = inject(AppSearchResultsService);
  private itemListProductGroupingStrategyPipe = inject(ItemListProductGroupingStrategyPipe);
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private t = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  private cartPromotionsService = inject(CartPromotionsService);

  readonly filtersDialog = viewChild(ProductFiltersDialogComponent);

  readonly category = signal<ApiV1_Category | null>(null);
  readonly isCartDisabled = signal<boolean>(false);
  readonly selectedTagIds = signal<string[]>([]);

  readonly displaySku = computed(() => this.appRequestContextService.shouldDisplaySku());
  readonly displayShareButton = computed(() => {
    return this.establishment().seoSocialMedia
      ? this.establishment().seoSocialMedia.displayShareButton
      : true;
  });
  readonly currencySymbol = computed(
    () => this.appRequestContextService.currencyLocaleData().currencySymbol,
  );
  readonly selectedLanguage = computed(() => this.appRequestContextService.selectedLanguage());
  readonly groupingStrategy = computed(() => this.category()?.dynamicGrouping || 'product-group');

  readonly isSingleActiveCategory = computed(
    () => this.appRequestContextService.activeCategories().length === 1,
  );
  readonly establishment = computed(() => this.appRequestContextService.establishment());
  readonly categoryId = computed(() => this.category()?.id);
  readonly categories = computed(() => this.appRequestContextService.allCategories());
  readonly products = computed(
    () =>
      this.appRequestContextService
        .allCategoriesWithActiveProducts()
        .find(category => category.id === this.categoryId())?.products ?? [],
  );
  readonly filteredProducts = computed(() =>
    this.searchResultsService.getFilteredResults(this.products(), {
      productTagIds: this.selectedTagIds(),
    }),
  );
  readonly productTagsWithQuantities = computed(() => getUniqueTagsWithQuantities(this.products()));
  readonly productsByGroup = computed(() => {
    return this.itemListProductGroupingStrategyPipe.transform(this.filteredProducts(), {
      groupingStrategy: this.groupingStrategy(),
      categories: this.categories(),
      undefinedGroupName: this.t.translate('product.undefinedGroupName'),
    });
  });
  readonly shareUrl = computed(() => {
    const subsidiarySelector = this.appRequestContextService.subsidiarySelector();
    const establishment = subsidiarySelector
      ? this.appRequestContextService.subsidiaryParentCatalog()?.establishment
      : this.establishment();

    return this.establishmentUrlPipe.transform(establishment, ['categoria', this.category()?.slug]);
  });

  constructor() {
    effect(() => {
      this.seoService.setCategoryMetadata(
        this.category(),
        this.appRequestContextService.establishment(),
      );
    });
    effect(() => {
      this.analytics.listCategoryProducts(this.category());
    });
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ category }) => {
      this.category.set(category);
    });

    this.establishmentWorkingHours.openedStatus$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isCartDisabled }) => {
        this.isCartDisabled.set(isCartDisabled);
      });
  }

  onFilterProducts() {
    const filtersDialog = this.filtersDialog();

    if (filtersDialog) {
      filtersDialog.open();
    }
  }

  onSelectedTags(selectedTags: string[]) {
    this.selectedTagIds.set(selectedTags);
  }

  getPromotionsForProduct(productId: string) {
    return this.cartPromotionsService.availablePromotionsByProductId().get(productId) ?? [];
  }
}
